import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE } from "./Reveal";
import { scrollToId } from "@/lib/scroll";
import { trackAuditCta } from "@/tracking/analytics";
import { useBooking } from "./BookingProvider";

const HERO_IMAGE =
    "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTEyMDc5NjB8MA&ixlib=rb-4.1.0&q=80&auto=format&fit=crop&w=1400";

const TITLE_LINES = [
    <>Turn Legal Searches</>,
    <>
        Into <span className="text-brass-400">Qualified</span>
    </>,
    <>
        <span className="text-brass-400">Consultations.</span>
    </>,
];

const CHANNELS = ["Meta Ads", "Conversion Tracking", "Landing Pages"];

export const Hero = () => {
    const ref = useRef(null);
    const openBooking = useBooking();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    const handlePrimary = (e) => {
        e.preventDefault();
        trackAuditCta("hero");
        openBooking();
    };
    const handleSecondary = (e) => {
        e.preventDefault();
        scrollToId("how-it-works");
    };

    return (
        <section
            ref={ref}
            id="top"
            className="relative min-h-[100svh] flex items-center pt-28 pb-20 md:pt-32 md:pb-28 overflow-hidden"
            aria-label="Introduction"
        >
            <motion.div
                style={{ y: glowY }}
                aria-hidden="true"
                className="absolute -top-40 -left-40 h-[560px] w-[560px] rounded-full bg-brass-500/[0.07] blur-[140px] pointer-events-none"
            />
            <div className="shell relative grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                        className="eyebrow mb-7"
                    >
                        Performance Marketing for U.S. Law Firms
                    </motion.p>
                    <h1 className="font-serif font-medium tracking-tight leading-[1.06] text-[2.6rem] sm:text-6xl lg:text-7xl xl:text-[5.25rem] text-white">
                        {TITLE_LINES.map((line, i) => (
                            <span
                                key={i}
                                className="block overflow-hidden pb-1 -mb-1"
                            >
                                <motion.span
                                    className="block will-change-transform"
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        duration: 1,
                                        delay: 0.18 + i * 0.14,
                                        ease: EASE,
                                    }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.62, ease: EASE }}
                        className="mt-7 max-w-xl text-base lg:text-lg leading-relaxed text-zinc-400"
                    >
                        Scalehaus helps U.S. law firms generate high-intent
                        leads through Google Ads, Meta Ads, conversion
                        tracking, and performance-focused landing pages.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.78, ease: EASE }}
                        className="mt-10 flex flex-col sm:flex-row gap-4"
                    >
                        <a
                            href="#audit"
                            data-testid="hero-primary-cta"
                            onClick={handlePrimary}
                            className="btn-brass group"
                        >
                            Explore My Options
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                        <a
                            href="#how-it-works"
                            data-testid="hero-secondary-cta"
                            onClick={handleSecondary}
                            className="btn-outline"
                        >
                            See How It Works
                        </a>
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9, delay: 1.05 }}
                        className="mt-9 text-sm text-zinc-500 flex items-center gap-3"
                        data-testid="hero-credibility-line"
                    >
                        <span
                            aria-hidden="true"
                            className="inline-block h-px w-8 bg-brass-500/60"
                        />
                        Built for law firms. Focused on measurable growth.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9, delay: 1.15 }}
                        className="mt-6 flex flex-wrap items-center gap-2 lg:hidden"
                    >
                        <span className="text-[11px] uppercase tracking-[0.2em] text-brass-400 border border-brass-500/40 px-3 py-1.5">
                            Primary Channel: Google Ads
                        </span>
                        {CHANNELS.map((c) => (
                            <span
                                key={c}
                                className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 border border-white/10 px-3 py-1.5"
                            >
                                {c}
                            </span>
                        ))}
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
                    className="hidden lg:block lg:col-span-5 relative"
                >
                    <motion.figure
                        style={{ y: imgY }}
                        className="relative overflow-hidden border border-white/10 aspect-[4/5]"
                        data-testid="hero-image"
                    >
                        <img
                            src={HERO_IMAGE}
                            alt="Abstract architectural geometry, the structure behind measurable growth"
                            className="h-full w-full object-cover grayscale brightness-[0.62] contrast-125 scale-110"
                            loading="eager"
                        />
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 bg-gradient-to-t from-ink-900 via-transparent to-ink-900/40"
                        />
                        <div
                            aria-hidden="true"
                            className="absolute inset-3 border border-brass-500/25 pointer-events-none"
                        />
                    </motion.figure>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
                        className="absolute -bottom-8 -left-10 bg-ink-800/95 backdrop-blur border border-white/10 p-6 w-72"
                        data-testid="hero-channel-panel"
                    >
                        <p className="text-[11px] uppercase tracking-[0.22em] text-brass-400 font-semibold">
                            Primary Channel
                        </p>
                        <p className="font-serif text-2xl text-white mt-2">
                            Google Ads
                        </p>
                        <div className="h-px bg-white/10 my-4" />
                        <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                            Supported by
                        </p>
                        <p className="text-xs text-zinc-400 leading-relaxed">
                            Meta Ads · Conversion Tracking · Landing Pages
                        </p>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
